<template>
    <div class="group-details-view">
        <a class="back-button" title="Back" @click.prevent="goBack">
            <div class="playbutton">
                <svgicon
                    class="mirror"
                    name="arrow_forward"
                    width="4rem"
                    height="4rem"
                    color="#ffffff"
                ></svgicon>
            </div>
            <span class="button-text">BACK</span>
        </a>
        <div
            class="details md-layout md-alignment-center-center"
            :class="{
                'poster-only': !group.displayPosterTitle && !group.description,
            }"
        >
            <div
                v-if="group.displayPosterTitle || group.description"
                class="details-pane md-layout-item md-xsmall-size-100 md-size-50"
            >
                <h1 v-if="group.displayPosterTitle" class="title">
                    {{ group.title }}
                </h1>
                <h2 v-if="group.displayPosterTitle" class="sub-title">
                    {{ group.subTitle }}
                </h2>
                <!--eslint-disable-next-line vue/no-v-html -->
                <p class="description" v-html="safeDescription" />
            </div>
            <div
                class="details-pane md-layout-item md-xsmall-size-100 md-size-50"
            >
                <div
                    role="image"
                    class="poster"
                    :alt="group.title"
                    :style="{
                        'background-image':
                            'url(&quot;' + group.fhdImage + '&quot;)',
                    }"
                ></div>
            </div>
        </div>
        <div v-if="displayType === 'rows'" class="row-container">
            <ProgramGroupRow
                v-for="(row, i) in nonEmptyGroups"
                :key="i"
                :group-id="row.id"
                :title="row.title"
                :has-user-suggestions="row.hasUserSuggestions"
                :item-style="getStyle(row)"
            />
        </div>
        <ProgramGroupGrid v-else :group-id="gridGroupId" :items="gridItems" />
    </div>
</template>

<script>
import ProgramGroupRow from '@/components/ProgramGroupRow.vue';
import ProgramGroupGrid from '@/components/ProgramGroupGrid.vue';

export default {
    name: 'ProgramGroup',
    components: {
        ProgramGroupRow,
        ProgramGroupGrid,
    },
    props: {
        groupId: {
            type: String,
            default: '',
        },
    },
    data: () => ({
        loading: true,
        group: {},
        programGroups: [],
        programItems: [],
    }),
    computed: {
        nonEmptyGroups: function () {
            return (
                this.programItems &&
                this.programItems.filter(
                    (group) => group.type === 'group' && group.childCount > 0,
                )
            );
        },
        videoCount: function () {
            return (
                (
                    this.programItems &&
                    this.programItems.filter((video) => video.type === 'video')
                )?.length || 0
            );
        },
        gridGroupId: function () {
            if (this.loading) {
                return undefined;
            }
            if (this.videoCount && !this.nonEmptyGroups?.length) {
                //this group only has videos so no need to fetch them again
                //so we will hand the videos off to the programGroupGrid
                return undefined;
            }
            //this group only has one row so display it as a grid
            if (!this.videoCount && this.nonEmptyGroups?.length === 1) {
                return this.nonEmptyGroups[0].id;
            }

            return this.groupId;
        },
        gridItems: function () {
            if (this.loading) {
                return undefined;
            }
            if (this.videoCount && !this.nonEmptyGroups?.length) {
                //this group only has videos so no need to fetch them again
                //so we hand them off to the programGroupGrid
                return this.programItems;
            }
            return undefined;
        },
        displayType: function () {
            // display rows if there is more than one group
            // otherwise for no groups, items are displayed as a grid
            // and for one group, that group is displayed as a grid
            if (this.nonEmptyGroups && this.nonEmptyGroups.length > 1) {
                return 'rows';
            } else {
                return 'grid';
            }
        },
        safeDescription: function () {
            let description =
                this.group?.webDescription || this.group?.description || '';
            return description.replace(/<\s*\/*\s*(script)([^>]*)>/gim, '');
        },
    },
    watch: {
        groupId: function () {
            this.loadGroup();
            this.loadGroupChildren();
        },
    },
    async created() {
        await Promise.all([this.loadGroup(), this.loadGroupChildren()]);
    },
    methods: {
        goBack() {
            this.$router.go(-1);
        },
        getStyle(row) {
            return row.rowStyle === 'default' ? 'poster' : row.rowStyle;
        },
        async loadGroup() {
            try {
                const group = await this.$gideo.getProgramGroup(this.groupId);
                this.group = group.data;
            } catch (e) {
                if (e.response && e.response.status === 404) {
                    this.$router.push({ name: 'notFound' });
                } else {
                    //console.error(e);
                }
            }
        },
        async loadGroupChildren() {
            this.loading = true;
            try {
                const programItems = await this.$gideo.getProgramGroups(
                    this.groupId,
                );
                this.programItems = programItems.data;
                this.programGroups = this.programItems.filter(
                    (group) => group.type === 'group',
                );
            } catch (e) {
                this.programItems = [];
                this.programGroups = [];
            }
            this.loading = false;
        },
    },
};
</script>
<style lang="scss">
@use 'sass:math';
@import '@/assets/css/_variables.scss';
.group-details-view {
    .body--light & a.back-button {
        color: $body-color-light;
        .playbutton {
            border-color: $body-color-light;
            svg path {
                fill: $body-color-light;
            }
        }
        span {
            color: $body-color-light;
            transition: color ease 0.4s;
        }
    }
}
</style>
<style scoped lang="scss">
@use 'sass:math';
@use 'sass:color';
@import '@/assets/css/_variables.scss';

.group-details-view {
    overflow: hidden;
    .body--light & {
        border-color: color.scale($background-color-light, $lightness: -35%);
        background-color: color.scale(
            $background-color-light,
            $lightness: -6.5%
        );
    }
    @media (min-width: 1280px) {
        position: relative;
    }
    .back-button {
        display: none;
        cursor: pointer;
        text-decoration: none;
        position: absolute;
        color: inherit;
        z-index: 1100;
        top: 2rem;
        left: 2rem;

        @media (min-width: 1280px) {
            display: block;
        }
        svg {
            transform: rotate(180deg);
        }
        .button-text {
            display: block;
            text-align: center;
            font-size: 1.2rem;
            line-height: 1em;
            margin-top: 0.5em;
        }
        .playbutton {
            transition: ease 0.3s;
        }
        &:hover {
            .playbutton {
                transform: translate(-5%);
            }
        }
    }
}

.poster {
    width: 100%;
    height: 0;
    padding-bottom: math.div(900%, 16);
    background-size: cover;
    transform-origin: bottom;
    transition: transform 0.35s 0.2s ease;
    position: relative;
    backface-visibility: hidden;
}
.details {
    position: relative;
    padding: 2.8rem 4.8rem 0;
    text-align: left;
    border-bottom: 0.1rem solid;
    border-color: color.scale($background-color, $lightness: 20%);
    font-size: 1.4rem;
    .body--light & {
        border-color: color.scale($background-color-light, $lightness: -35%);
        background-color: $background-color-light;
    }

    @media (min-width: 600px) {
        padding: 2.8rem 4.8rem;
    }
    &.poster-only {
        padding: 0;
    }

    @media (min-width: 1280px) {
        padding: 4rem 10rem;
        &.poster-only {
            padding: 0 10rem;
        }
    }

    .title {
        font-size: 2em;
        font-weight: 500;
        line-height: 1.1em;
        text-align: center;
        margin: 0 0 0.24em;

        @media (min-width: 600px) {
            font-size: 1.6em;
            text-align: left;
        }
        @media (min-width: 1280px) {
            font-size: 2em;
        }
    }
    .sub-title {
        text-align: center;
        font-size: 1.6em;
        font-weight: 500;
        line-height: 1.1em;
        margin: 0.5em 0 0.3em;

        @media (min-width: 600px) {
            font-size: 1.2em;
            text-align: left;
        }
        @media (min-width: 1280px) {
            font-size: 1.6em;
        }
    }
    .description {
        font-size: 1em;
        line-height: 1.3em;
        margin-bottom: 1em;
        @media (min-width: 600px) {
            font-size: 0.8em;
        }
        @media (min-width: 1280px) {
            font-size: 1em;
            text-align: justify;
        }
    }
    .details-pane:first-child {
        order: 1;
        padding: 2rem 0;
        align-self: start;
        @media (min-width: 600px) {
            padding: 0 4.8rem 0 0;
            order: 0;
        }
        @media (min-width: 1280px) {
            padding: 0 10rem 0 0;
        }
    }

    &.poster-only {
        .details-pane:first-child {
            padding: 0;
            @media (min-width: 1280px) {
                flex: 0 1 20%;
                min-width: unset;
            }
        }
    }
}
</style>
